import React from 'react';
import { BrowserTabTitle } from '../BrowserTabTitle';
import { capitalize } from '../../utils/strings';
import { useEntities } from '../../data/flowEntities';
import { useAccount } from '../../auth';
import { twentyFourHoursAgo } from './utils';
import { gridContainerMinWidth } from '../../utils/theme';
import { useGlobalSettings } from '../../app/GlobalSettingProvider';
import getFlowsListColumns from './FlowsListColumnsDef';
import AQDataGrid, { AQDataGridQueryParams } from '../AQDataGrid';
import { FlowJobsGroup } from './flows.types';
import { PageTitle } from '../PageTitle';
import { TimeZoneSelector } from '../header/TimeZoneSelector';
import { theme } from '../../utils/theme';

export const FlowsList = () => {
  const { currentAccount } = useAccount();

  const pluralEntityType = 'flows';
  const entityType = 'flow';

  const { settings } = useGlobalSettings();

  const flowJobsQueryParams = React.useMemo(
    () => ({
      page: 1,
      size: 10000,
      filter: {
        job_type: 'flow',
        updated_at__gte: twentyFourHoursAgo.toISOString(),
      },
    }),
    [currentAccount.id],
  );

  const flowJobsQuery = useEntities(
    currentAccount.id,
    'job',
    flowJobsQueryParams,
    // TODO: this could be dynamically set to the shortest cron schedule of a job
    // { refetchInterval: 3000 },
  );

  // Explicitly type the object to avoid implicit 'any' errors
  const flowJobsGroupedByFlowId: { [flowId: string]: FlowJobsGroup } = {};
  if (!flowJobsQuery.isLoading) {
    const jobs = flowJobsQuery?.data?.getData() || [];

    jobs.forEach((job: any) => {
      if (!flowJobsGroupedByFlowId[job.entity_id]) {
        flowJobsGroupedByFlowId[job.entity_id] = {
          flow_id: job.entity_id,
          [job.entity_id]: job.entity_id,
          counts: {},
          jobs: [],
        };
      }

      if (!flowJobsGroupedByFlowId[job.entity_id].counts[job.status]) {
        flowJobsGroupedByFlowId[job.entity_id].counts[job.status] = 0;
      }

      flowJobsGroupedByFlowId[job.entity_id].counts[job.status] += 1;

      flowJobsGroupedByFlowId[job.entity_id].jobs.push(job);
    });
  }

  const buildQueryArgs = React.useCallback(
    (queryParams: AQDataGridQueryParams) => [
      currentAccount.id,
      entityType,
      {
        include: [
          'health-status',
          'last-run',
          'next-run',
          'source',
          'destinations',
        ],
        ...queryParams,
      },
    ],
    [currentAccount.id, entityType],
  );

  const rowsTransform = React.useCallback(
    (entities: any[]) =>
      entities.map((entity: any) => ({
        ...entity,
        id: entity.id,
        source: entity?.source,
        destinations: entity?.destinations
          ? new Set(entity?.destinations)
          : null,
        nextRun: entity?.['next-run'],
        lastRun: entity?.['last-run'],
        health: entity?.['health-status']?.status || 'unknown',
        jobsCounts: flowJobsGroupedByFlowId?.[entity.id]?.counts || {},
        entity_type: entity?.entity_type,
        type_name: entity?.type_name,
        source_img_url: entity?.source?.img_url,
      })),
    [flowJobsGroupedByFlowId],
  );

  const colDefs = React.useMemo(
    () => getFlowsListColumns(settings, currentAccount),
    [settings, currentAccount],
  );

  const gridProps = React.useMemo(
    () => ({
      getRowClassName: (params: { row: any }) =>
        `aq-datagrid-row--${params.row.health}`,
      getEstimatedRowHeight: () => 60,
      getRowHeight: () => 'auto',
    }),
    [],
  );

  return (
    <div>
      <BrowserTabTitle>{capitalize(pluralEntityType)}</BrowserTabTitle>
      <PageTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: theme.spacing(8),
        }}
      >
        <TimeZoneSelector />
      </PageTitle>
      <AQDataGrid
        dataDependencies={[flowJobsQuery]}
        dataQueryHook={useEntities}
        buildQueryArgs={buildQueryArgs}
        rowsTransformer={rowsTransform}
        colDefs={colDefs}
        gridProps={gridProps}
        sx={{
          width: gridContainerMinWidth,
          margin: '0 auto',
          // adding padding to auto height cells
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
            py: '15px',
          },
          '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
            py: '22px',
          },
        }}
      />
    </div>
  );
};
